import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { get } from 'lodash'
import * as Sentry from '@sentry/browser'
import { jwtDecode } from 'jwt-decode'
import { Spinner } from '@ui'

import { setTag } from '@sentry/browser'
import { getValidAccessToken } from '../features/auth'
import { AppLayout, BlankLayout } from '../layouts'
import cookieStorage from '../features/cookie'
import { useCurrentMerchant, useCurrentMerchantCountryCode, useCurrentUserMerchantRoles, useJWT } from '../hooks'
import { getCurrentMerchantID } from '../features/data'
import { setMerchantIdAndCountryCodeCookies } from '../utils'
import { getDefaultMarketCountryCode } from '../services/marketMetadata'

const getLoginRedirectTarget = location => ({
	search: new URLSearchParams({ redirectUrl: location.pathname + location.search }).toString(),
	pathname: '/login',
})

const MerchantAccessGate = ({ children }) => {

	const {
		roles,
		allMerchants,
		rolesExists,
		connectedToOnlyOneMerchant,
		onlyOneConnectedMerchantId,
		loading: merchantRolesLoading,
	} = useCurrentUserMerchantRoles()

	const [merchantIsSelected, setMerchantIsSelected] = useState(false)
	const { jwt } = useJWT()
	const location = useLocation()
	const navigate = useNavigate()

	// Only process merchantId parameter on the first render, therefore with empty dependency on useEffect
	useEffect(() => {
		const queryParams = new URLSearchParams(location.search)
		if (allMerchants && queryParams.has('merchantId')) {
			cookieStorage.setItem('currentMerchantID', queryParams.get('merchantId'))
			queryParams.delete('merchantId')
			if (queryParams.has('country')) {
				cookieStorage.setItem('currentMerchantCountryCode', queryParams.get('country'))
				queryParams.delete('country')
			} else {
				cookieStorage.setItem('currentMerchantCountryCode', getDefaultMarketCountryCode())
			}
			navigate({ search: queryParams.toString() }, { replace: true })
		}
	}, [])

	const { currentMerchantID } = getCurrentMerchantID()
	const { data } = useCurrentMerchant()
	const [cookiesAreSet, setCookiesAreSet] = useState(false)
	const { currentMerchantCountryCode } = useCurrentMerchantCountryCode()

	useEffect(() => {		
		if (rolesExists) {
			if (!currentMerchantID) {
				const merchant = get(roles, '0.merchant', undefined)
				if (merchant) {
					setMerchantIdAndCountryCodeCookies(merchant.id, merchant.country)
				}
			}
		}

		if (roles) setMerchantIsSelected(true)
	}, [currentMerchantID, roles])

	useEffect(() => {
		if (data && connectedToOnlyOneMerchant) {
			const id = get(data, 'merchant.id')
			const country = get(data, 'merchant.country')
			setMerchantIdAndCountryCodeCookies(id, country)
			setCookiesAreSet(true)
			setTag('currentMerchantID', id)
			setTag('currentMerchantCountryCode', country)
		}
	}, [data, connectedToOnlyOneMerchant])

	useEffect(() => {
		if (connectedToOnlyOneMerchant) {
			cookieStorage.setItem('currentMerchantID', onlyOneConnectedMerchantId())
		}
	}, [connectedToOnlyOneMerchant])

	useEffect(() => {
		if (!jwt) return
		const { userID } = jwtDecode(jwt)
		Sentry.setUser({ id: `UserID: ${userID}` })
		Sentry.setTag('currentMerchantID', currentMerchantID)
		Sentry.setTag('currentMerchantCountryCode', currentMerchantCountryCode)
	}, [jwt, Sentry, currentMerchantID])

	if (merchantRolesLoading || !merchantIsSelected) return <Spinner />
	if (connectedToOnlyOneMerchant && !cookiesAreSet) return <Spinner />

	if (rolesExists && !connectedToOnlyOneMerchant) return children
	if (rolesExists && connectedToOnlyOneMerchant && cookiesAreSet) return children
	if (!rolesExists) return children
	return (
		<Navigate
			to={{
				search: new URLSearchParams({ error: 'NOT_CONNECTED_TO_MERCHANT' }).toString(),
				pathname: '/login',
			}}
			replace
		/>
	)
}

MerchantAccessGate.propTypes = {
	children: PropTypes.node.isRequired,
}

const RedirectIfUnAuthorized = ({ children }) => {
	const location = useLocation()
	const [authState, setAuthState] = useState({
		isResolved: false,
		accessToken: null,
	})

	useEffect(() => {
		let isMounted = true

		setAuthState({
			isResolved: false,
			accessToken: null,
		})

		getValidAccessToken().then(accessToken => {
			if (!isMounted) return
			setAuthState({
				isResolved: true,
				accessToken,
			})
		})

		return () => {
			isMounted = false
		}
	}, [location.pathname, location.search])

	if (!authState.isResolved) {
		return <Spinner />
	}

	if (!authState.accessToken) {
		return <Navigate to={getLoginRedirectTarget(location)} replace />
	}

	return <MerchantAccessGate>{children}</MerchantAccessGate>
}

RedirectIfUnAuthorized.propTypes = {
	children: PropTypes.node.isRequired,
}

const renderWithLayout = (element, layout) =>
	({
		AppLayout: (
			<AppLayout>
				{element}
			</AppLayout>
		),
		BlankLayout: (
			<BlankLayout>
				{element}
			</BlankLayout>
		),
	}[layout] || element)

const PrivateRoute = ({ element, layout }) => (
	<RedirectIfUnAuthorized>{renderWithLayout(element, layout)}</RedirectIfUnAuthorized>
)

PrivateRoute.propTypes = {
	layout: PropTypes.string,
	element: PropTypes.node.isRequired,
}

export default PrivateRoute
